import { Link } from "@tanstack/react-router";
import { salons } from "@/data/salons";
import { internalPath } from "@/lib/paths";
import { serviceBookingUrlAt } from "@/lib/serviceBooking";
import type { Service } from "@/data/types";

/**
 * Internal-linking block on service pages. Routes users from topical service
 * pages into the strongest local pages (Täby Centrum and Åhléns City first),
 * with keyword-rich anchor text, then the remaining salons.
 */

const PRIORITY = ["taby", "ahlens-city"] as const;

const ANCHOR: Record<string, { anchor: string; blurb: string }> = {
  taby: {
    anchor: "Barberare Täby Centrum",
    blurb:
      "Vår mest bokade salong i Westfield Täby Centrum – erfarna barberare, drop-in i mån av plats.",
  },
  "ahlens-city": {
    anchor: "Barber Stockholm City",
    blurb:
      "Barberare Stockholm City i Åhléns på Klarabergsgatan, en minut från T-Centralen.",
  },
};

export function LocalSalonLinks({ service }: { service: Service }) {
  const ordered = [
    ...PRIORITY.map((slug) => salons.find((s) => s.slug === slug)).filter(Boolean),
    ...salons.filter((s) => !PRIORITY.includes(s.slug as (typeof PRIORITY)[number])),
  ] as typeof salons;

  const offered = ordered.filter((s) => service.availableAt.includes(s.slug));
  if (offered.length === 0) return null;

  const serviceLabel = service.shortName.toLowerCase();

  return (
    <section
      className="container-page py-12 md:py-16 border-t border-border"
      aria-labelledby="local-salon-links-heading"
    >
      <div className="max-w-2xl mb-8">
        <div className="eyebrow">Hitta närmaste salong</div>
        <h2 id="local-salon-links-heading" className="mt-3 text-2xl md:text-3xl">
          {service.shortName} nära dig i Stockholm
        </h2>
        <p className="mt-3 text-muted-foreground">
          Välj salong och boka {serviceLabel} hos en barberare i Täby, Stockholm City,
          Arninge eller Åkersberga.
        </p>
      </div>

      <ul className="grid gap-4 sm:grid-cols-2">
        {offered.map((s) => {
          const meta = ANCHOR[s.slug];
          const href = s.url;
          return (
            <li key={s.slug} className="card-elev p-5 h-full hover:border-brass transition-colors">
              <Link
                to={internalPath(href)}
                className="text-lg font-semibold text-brass-deep hover:underline"
              >
                {meta?.anchor ?? `Barberare ${s.shortName}`}
              </Link>
              <span className="block text-sm text-muted-foreground mt-1">
                {meta?.blurb ?? s.description}
              </span>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <Link to={internalPath(href)} className="hover:underline">
                  {service.shortName} i {s.shortName} →
                </Link>
                <a
                  href={serviceBookingUrlAt(service, s.slug)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cta="local_salon_links"
                  className="font-medium text-brass-deep hover:underline"
                >
                  Boka i {s.shortName} på BokaDirekt
                </a>
              </div>
            </li>
          );
        })}
      </ul>


      <p className="mt-6 text-sm text-muted-foreground">
        Se alla{" "}
        <Link to={"/"} className="underline font-medium">
          barberare i Stockholm
        </Link>{" "}
        eller läs mer om{" "}
        <Link to={internalPath("/barberare-stockholm-taby")} className="underline font-medium">
          Barberare Täby
        </Link>{" "}
        och{" "}
        <Link
          to={internalPath("/barberare-stockholm-ahlens-city")}
          className="underline font-medium"
        >
          Barberare Stockholm City
        </Link>
        .
      </p>
    </section>
  );
}
